import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  HStack,
  Text,
  VStack,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { ExtendedFieldDetailForm } from "../../forms/ExtendedFieldDetailForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { FieldPreview } from "../../components/ExtendedFields/FieldPreview";

interface ExtendedFieldDetailProps {
  create?: boolean;
}

export const ExtendedFieldDetail = (props: ExtendedFieldDetailProps) => {
  const [searchParams] = useSearchParams();
  const { businessId, fieldId } = useParams();
  const [field, setField] =
    useState<CherryHubAdminApi.ExtendedFieldDefinition | null>();
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { data: fieldDoc, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !fieldId) {
        return null;
      }
      return apiClient.getBusinessExtendedField(businessId, fieldId);
    },
    [businessId, fieldId]
  );
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  useEffect(() => {
    setField(fieldDoc);
  }, [fieldDoc]);

  const onSuccess = useCallback(
    (field: CherryHubAdminApi.ExtendedFieldDefinition) => {
      setField(field);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Extended field created.");
        navigate(
          `/admin/businesses/${business.BusinessId}/extended-fields/${field.id}`
        );
      } else {
        showSuccessToast("Extended field updated.");
      }
    },
    [showSuccessToast, field, navigate, business.BusinessId, props.create]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Extended field update failed.");
    },
    [showErrorToast]
  );

  const getFieldTypeLabel = (
    fieldType?: CherryHubAdminApi.ExtendedFieldType
  ) => {
    switch (fieldType) {
      case "Text":
        return "Text Input";
      case "TextArea":
        return "Text Area";
      case "TextSelection":
        return "Text Selection";
      case "TextMultiSelection":
        return "Text Multi-Selection";
      case "Number":
        return "Number Input";
      case "NumberSelection":
        return "Number Selection";
      case "NumberMultiSelection":
        return "Number Multi-Selection";
      case "Date":
        return "Date";
      case "DateTime":
        return "Date & Time";
      case "Time":
        return "Time";
      case "Toggle":
        return "Toggle (Checkbox)";
      default:
        return fieldType || "—";
    }
  };

  const getWritabilityLabel = (
    writability?: CherryHubAdminApi.FieldWritability
  ) => {
    switch (writability) {
      case "UserWritable":
        return "User Writable";
      case "SystemWritable":
        return "System Writable";
      case "ReadOnly":
        return "Read Only";
      default:
        return writability || "—";
    }
  };

  const formatDateForDisplay = (
    dateStr: string | undefined,
    fieldType?: CherryHubAdminApi.ExtendedFieldType
  ): string => {
    if (!dateStr) return "—";

    try {
      // For Time fields, extract time and format using locale (without timezone conversion)
      if (fieldType === "Time") {
        // Extract time from ISO string like "1970-01-01T14:30:00"
        const timeMatch = dateStr.match(/T(\d{2}):(\d{2})/);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);

          // Format using locale without timezone conversion
          // Create a date in local time (not UTC)
          const date = new Date(2000, 0, 1, hours, minutes);
          return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
        // Fallback if no match
        const simpleTimeMatch = dateStr.match(/^(\d{2}):(\d{2})/);
        if (simpleTimeMatch) {
          const hours = parseInt(simpleTimeMatch[1], 10);
          const minutes = parseInt(simpleTimeMatch[2], 10);
          const date = new Date(2000, 0, 1, hours, minutes);
          return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }

      // For Date fields, extract date components and format without timezone conversion
      if (fieldType === "Date") {
        // Extract date from ISO string like "2025-01-15T00:00:00" or "2025-01-15"
        const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1; // months are 0-indexed
          const day = parseInt(dateMatch[3], 10);

          // Create a date in local time (not UTC)
          const date = new Date(year, month, day);
          return date.toLocaleDateString();
        }
      }

      // For DateTime fields, extract date and time components and format without timezone conversion
      if (fieldType === "DateTime") {
        // Extract date and time from ISO string like "2025-01-15T14:30:00"
        const dateTimeMatch = dateStr.match(
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
        );
        if (dateTimeMatch) {
          const year = parseInt(dateTimeMatch[1], 10);
          const month = parseInt(dateTimeMatch[2], 10) - 1; // months are 0-indexed
          const day = parseInt(dateTimeMatch[3], 10);
          const hours = parseInt(dateTimeMatch[4], 10);
          const minutes = parseInt(dateTimeMatch[5], 10);

          // Create a date in local time (not UTC)
          const date = new Date(year, month, day, hours, minutes);
          return date.toLocaleString();
        }
      }

      return dateStr;
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Extended Field
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/extended-fields`}
              >
                Extended Fields
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/extended-fields/${
                  props.create ? "new" : field?.id
                }`}
              >
                {props.create ? "New" : field?.FieldName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || !field) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
              <SkeletonText w="100%" mt="4" noOfLines={4} spacing="4" />
            </VStack>
          )}
          {!isLoading && editMode && (
            <ExtendedFieldDetailForm
              field={field}
              apiClient={apiClient}
              businessId={business.BusinessId!}
              onCancel={() => {
                props.create
                  ? navigate(
                      `/admin/businesses/${business.BusinessId}/extended-fields`
                    )
                  : setEditMode(false);
              }}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}
          {!isLoading && !editMode && field && (
            <>
              <HStack w="100%" pb={4}>
                <Box flexGrow={1}>
                  <CopyText text={field?.id!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {field?.id}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{field?.FieldName}</b>
                  </Text>
                </Box>
                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <VStack
                w="100%"
                maxW="1200px"
                alignItems="flex-start"
                spacing={4}
              >
                <ViewControl label="Description" value={field?.Description} />

                <ViewControl label="Owner" value={field?.Owner} />

                <ViewControl
                  label="Field Type"
                  value={getFieldTypeLabel(field?.FieldType)}
                />

                <ViewControl
                  label="Writability"
                  value={getWritabilityLabel(field?.Writability)}
                />

                {field?.StringAttributes && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      String Field Configuration
                    </Text>

                    <ViewControl
                      label="Is Required"
                      value={field?.StringAttributes?.IsRequired ? "Yes" : "No"}
                    />

                    {(field?.FieldType === "Text" ||
                      field?.FieldType === "TextArea") && (
                      <>
                        {field?.StringAttributes?.MinLength !== null &&
                          field?.StringAttributes?.MinLength !== undefined && (
                            <ViewControl
                              label="Min Length"
                              value={field?.StringAttributes?.MinLength}
                            />
                          )}

                        {field?.StringAttributes?.MaxLength !== null &&
                          field?.StringAttributes?.MaxLength !== undefined && (
                            <ViewControl
                              label="Max Length"
                              value={field?.StringAttributes?.MaxLength}
                            />
                          )}

                        {field?.StringAttributes?.RegexPattern && (
                          <ViewControl
                            label="Regex Pattern"
                            value={field?.StringAttributes?.RegexPattern}
                          />
                        )}
                      </>
                    )}

                    {(field?.FieldType === "TextSelection" ||
                      field?.FieldType === "TextMultiSelection") &&
                      field?.StringAttributes?.AllowedValues &&
                      field?.StringAttributes?.AllowedValues.length > 0 && (
                        <ViewControl
                          label="Allowed Values"
                          value={field?.StringAttributes?.AllowedValues?.join(
                            ", "
                          )}
                        />
                      )}

                    {(() => {
                      const stringAttrs = field?.StringAttributes as any;
                      if (!stringAttrs) return null;

                      let defaultValue: string | undefined;

                      if (
                        stringAttrs.DefaultValues &&
                        Array.isArray(stringAttrs.DefaultValues) &&
                        stringAttrs.DefaultValues.length > 0
                      ) {
                        defaultValue = stringAttrs.DefaultValues.join(", ");
                      }

                      return defaultValue ? (
                        <ViewControl
                          label="Default Value"
                          value={defaultValue}
                        />
                      ) : null;
                    })()}
                  </>
                )}

                {field?.NumberAttributes && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      Number Field Configuration
                    </Text>

                    <ViewControl
                      label="Is Required"
                      value={field?.NumberAttributes?.IsRequired ? "Yes" : "No"}
                    />

                    {field?.FieldType === "Number" && (
                      <>
                        {field?.NumberAttributes?.DecimalPlaces !== null &&
                          field?.NumberAttributes?.DecimalPlaces !==
                            undefined && (
                            <ViewControl
                              label="Decimal Places"
                              value={field?.NumberAttributes?.DecimalPlaces}
                            />
                          )}

                        {field?.NumberAttributes?.MinValue !== null &&
                          field?.NumberAttributes?.MinValue !== undefined && (
                            <ViewControl
                              label="Min Value"
                              value={
                                field?.NumberAttributes?.DecimalPlaces !==
                                  null &&
                                field?.NumberAttributes?.DecimalPlaces !==
                                  undefined
                                  ? Number(
                                      field.NumberAttributes.MinValue
                                    ).toFixed(
                                      field.NumberAttributes.DecimalPlaces
                                    )
                                  : field?.NumberAttributes?.MinValue
                              }
                            />
                          )}

                        {field?.NumberAttributes?.MaxValue !== null &&
                          field?.NumberAttributes?.MaxValue !== undefined && (
                            <ViewControl
                              label="Max Value"
                              value={
                                field?.NumberAttributes?.DecimalPlaces !==
                                  null &&
                                field?.NumberAttributes?.DecimalPlaces !==
                                  undefined
                                  ? Number(
                                      field.NumberAttributes.MaxValue
                                    ).toFixed(
                                      field.NumberAttributes.DecimalPlaces
                                    )
                                  : field?.NumberAttributes?.MaxValue
                              }
                            />
                          )}
                      </>
                    )}

                    {(field?.FieldType === "NumberSelection" ||
                      field?.FieldType === "NumberMultiSelection") &&
                      field?.NumberAttributes?.AllowedValues &&
                      field?.NumberAttributes?.AllowedValues.length > 0 && (
                        <ViewControl
                          label="Allowed Values"
                          value={field?.NumberAttributes?.AllowedValues?.join(
                            ", "
                          )}
                        />
                      )}

                    {(() => {
                      const numberAttrs = field?.NumberAttributes as any;
                      if (!numberAttrs) return null;

                      const decimalPlaces = numberAttrs.DecimalPlaces;
                      let defaultValue: string | number | undefined;

                      if (
                        numberAttrs.DefaultValues &&
                        Array.isArray(numberAttrs.DefaultValues) &&
                        numberAttrs.DefaultValues.length > 0
                      ) {
                        if (
                          decimalPlaces !== null &&
                          decimalPlaces !== undefined
                        ) {
                          defaultValue = numberAttrs.DefaultValues.map(
                            (v: number) => Number(v).toFixed(decimalPlaces)
                          ).join(", ");
                        } else {
                          defaultValue = numberAttrs.DefaultValues.join(", ");
                        }
                      }

                      return defaultValue !== undefined ? (
                        <ViewControl
                          label="Default Value"
                          value={defaultValue}
                        />
                      ) : null;
                    })()}
                  </>
                )}

                {field?.DateAttributes && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      {field?.FieldType === "DateTime"
                        ? "Date & Time Field Configuration"
                        : "Date Field Configuration"}
                    </Text>

                    <ViewControl
                      label="Is Required"
                      value={field?.DateAttributes?.IsRequired ? "Yes" : "No"}
                    />

                    {field?.DateAttributes?.MinValue && (
                      <ViewControl
                        label={
                          field?.FieldType === "DateTime"
                            ? "Min Date & Time"
                            : "Min Date"
                        }
                        value={formatDateForDisplay(
                          field?.DateAttributes?.MinValue,
                          field?.FieldType
                        )}
                      />
                    )}

                    {field?.DateAttributes?.MaxValue && (
                      <ViewControl
                        label={
                          field?.FieldType === "DateTime"
                            ? "Max Date & Time"
                            : "Max Date"
                        }
                        value={formatDateForDisplay(
                          field?.DateAttributes?.MaxValue,
                          field?.FieldType
                        )}
                      />
                    )}

                    {field?.DateAttributes?.RelativeMinValue && (
                      <ViewControl
                        label={
                          field?.FieldType === "DateTime"
                            ? "Relative Min Date & Time"
                            : "Relative Min Date"
                        }
                        value={`${
                          field.DateAttributes.RelativeMinValue.Quantity
                        } ${field.DateAttributes.RelativeMinValue.Period}${
                          field.DateAttributes.RelativeMinValue.Quantity !== 1
                            ? "s"
                            : ""
                        }`}
                      />
                    )}

                    {field?.DateAttributes?.RelativeMaxValue && (
                      <ViewControl
                        label={
                          field?.FieldType === "DateTime"
                            ? "Relative Max Date & Time"
                            : "Relative Max Date"
                        }
                        value={`${
                          field.DateAttributes.RelativeMaxValue.Quantity
                        } ${field.DateAttributes.RelativeMaxValue.Period}${
                          field.DateAttributes.RelativeMaxValue.Quantity !== 1
                            ? "s"
                            : ""
                        }`}
                      />
                    )}

                    {field?.DateAttributes?.DefaultValue && (
                      <ViewControl
                        label="Default Value"
                        value={formatDateForDisplay(
                          field?.DateAttributes?.DefaultValue,
                          field?.FieldType
                        )}
                      />
                    )}
                  </>
                )}

                {field?.TimeAttributes && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      Time Field Configuration
                    </Text>

                    <ViewControl
                      label="Is Required"
                      value={field?.TimeAttributes?.IsRequired ? "Yes" : "No"}
                    />

                    {field?.TimeAttributes?.MinValue && (
                      <ViewControl
                        label="Min Time"
                        value={formatDateForDisplay(
                          field?.TimeAttributes?.MinValue,
                          field?.FieldType
                        )}
                      />
                    )}

                    {field?.TimeAttributes?.MaxValue && (
                      <ViewControl
                        label="Max Time"
                        value={formatDateForDisplay(
                          field?.TimeAttributes?.MaxValue,
                          field?.FieldType
                        )}
                      />
                    )}

                    {field?.TimeAttributes?.DefaultValue && (
                      <ViewControl
                        label="Default Value"
                        value={formatDateForDisplay(
                          field?.TimeAttributes?.DefaultValue,
                          field?.FieldType
                        )}
                      />
                    )}
                  </>
                )}

                {field?.BooleanAttributes && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      Toggle Field Configuration
                    </Text>

                    <ViewControl
                      label="Default Value"
                      value={
                        field?.BooleanAttributes?.DefaultValue
                          ? "Checked"
                          : "Unchecked"
                      }
                    />
                  </>
                )}

                {/* Field Preview Section */}
                {field?.FieldType && (
                  <>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="cherryUi.600"
                      pt={4}
                    >
                      Field Preview
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      This is an approximation of how the field will appear to
                      users.
                    </Text>
                    <FieldPreview
                      fieldType={field.FieldType}
                      fieldName={field.FieldName || ""}
                      description={field.Description || undefined}
                      minLength={field.StringAttributes?.MinLength ?? undefined}
                      maxLength={field.StringAttributes?.MaxLength ?? undefined}
                      regexPattern={
                        field.StringAttributes?.RegexPattern || undefined
                      }
                      stringAllowedValues={field.StringAttributes?.AllowedValues?.join(
                        ", "
                      )}
                      minValue={field.NumberAttributes?.MinValue ?? undefined}
                      maxValue={field.NumberAttributes?.MaxValue ?? undefined}
                      decimalPlaces={
                        field.NumberAttributes?.DecimalPlaces ?? undefined
                      }
                      numberAllowedValues={field.NumberAttributes?.AllowedValues?.join(
                        ", "
                      )}
                      dateMinValue={field.DateAttributes?.MinValue ?? undefined}
                      dateMaxValue={field.DateAttributes?.MaxValue ?? undefined}
                      timeMinValue={field.TimeAttributes?.MinValue ?? undefined}
                      timeMaxValue={field.TimeAttributes?.MaxValue ?? undefined}
                      booleanDefaultValue={
                        field.BooleanAttributes?.DefaultValue
                      }
                    />
                  </>
                )}
              </VStack>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
